// California Institute of Technology: public college page (#2652), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Caltech's Common Data Set 2025-2026 (finance.caltech.edu, cds_2026.pdf;
//    its cover and page headers say "Common Data Set 2025-2026"): C1
//    applicants/admits/enrollees for Fall 2025, C8 test policy for Fall 2027,
//    C21/C22 plans. Quirks: the PDF's text layer uses a shifted font for some
//    words, so the checkboxes were read on the rendered pages; the C1
//    residency table is filled with zeros, so there are no residency rows.
//  - admissions.caltech.edu: First-Year Application Deadlines (Restrictive
//    Early Action November 1, 2026; Regular Decision January 4, 2027) and
//    Standardized Tests (the fall 2027 policy and score deadlines). Quirk:
//    one Regular Decision paragraph says admitted students reply by "May 1,
//    2026"; every other line on the page says May 1, 2027.
//  - Caltech Catalog 2026-2027, Undergraduate Expenses: the 2026-27 on-campus
//    cost of attendance. The Financial Aid Office site (finaid.caltech.edu)
//    blocked scripted reads; the catalog's figures match the CDS G1/G5
//    except required fees, where the catalog uses $2,655 for all
//    undergraduates and the CDS lists $3,255 for first-years (the $600 is the
//    one-time orientation fee, in the note).
//  - College Scorecard school page, id 110404 (net price by family income;
//    the shared DEMO_KEY was rate-limited, so the figures are the ones the
//    official page loads from the same API).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Caltech Common Data Set 2025-2026',
        url: 'https://finance.caltech.edu/documents/35127/cds_2026.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Caltech Undergraduate Admissions, First-Year Application Deadlines',
        url: 'https://www.admissions.caltech.edu/apply/first-year-applicants/deadlines',
        read: '2026-10-01',
    },
    tests: {
        label: 'Caltech Undergraduate Admissions, Standardized Tests',
        url: 'https://www.admissions.caltech.edu/apply/first-year-applicants/standardized-tests',
        read: '2026-10-01',
    },
    expenses: {
        label: 'Caltech Catalog 2026-2027, Undergraduate Expenses',
        url: 'https://catalog.caltech.edu/current/information-for-undergraduate-students/undergraduate-expenses/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110404-California-Institute-of-Technology',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'california-institute-of-technology',
    kbId: 'california_institute_of_technology',
    name: 'California Institute of Technology',
    shortName: 'Caltech',
    location: 'Pasadena, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Caltech identity colors); decoration only.
    branding: {
        primary: '#FF6C0C',
        secondary: '#FF6C0C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/california_institute_of_technology_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 11285,
        admitted: 428,
        enrolled: 248,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Caltech\'s Common Data Set reports one first-year acceptance rate for the whole institute, with no rate by option (Caltech\'s word for a major).',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Restrictive early action',
        note: 'Caltech has no early decision plan. The Common Data Set lists its Restrictive Early Action dates (November 1, notification December 15) but gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Restrictive Early Action', deadline: 'November 1', decision: 'By mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'By mid-March', binding: false },
        ],
        note: 'Required and supplemental materials can follow by November 6 for Restrictive Early Action and January 10 for Regular Decision. SAT or ACT scores are due before November 30 and December 31. Admitted students have until May 1, 2027 to decide. QuestBridge applicants follow a separate process.',
        source: 'deadlines',
        binding: 'Restrictive Early Action is not binding: if admitted, you have until May 1, 2027 to decide. While you apply this way, you may not apply early action or early decision anywhere else, with exceptions for schools outside the United States, public universities with a non-binding fall deadline (such as the University of California), non-binding rolling admission, military academies, and non-binding early programs required for a scholarship or special program. If you are deferred, you may apply to another school\'s Early Decision II. A denial is final for that cycle.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Caltech requires the SAT or ACT. It does not use the composite: it reads each section score, and a section score of 750 or more on the SAT (33 or more on the ACT) is shown to readers only as a range. Caltech superscores, has no minimum score and no preferred test, and asks for official scores only if you enroll.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants, with scores due by December 31.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 68574 },
            { item: 'Fees', amount: 2655 },
            { item: 'Housing', amount: 12711 },
            { item: 'Food', amount: 9285 },
            { item: 'Books, course materials, supplies and equipment', amount: 1428 },
            { item: 'Personal expenses', amount: 3969 },
        ],
        total: 98622,
        note: 'For a student living in Caltech housing. Entering students also pay a one-time $600 orientation fee. Fees do not include Caltech\'s student health insurance, and a travel allowance can be added for U.S. citizens and eligible noncitizens living in the U.S., its territories, Canada or Mexico.',
        source: 'expenses',
        netPrice: {
            average: 8509,
            byIncome: [
                { income: '$0 to $30,000', amount: 7553 },
                { income: '$30,001 to $48,000', amount: 11452 },
                { income: '$48,001 to $75,000', amount: 4144 },
                { income: '$75,001 to $110,000', amount: 29762 },
                { income: 'Over $110,000', amount: null },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the over $110,000 band. Your own price comes from Caltech\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
